import {
  GridType,
  LOCATION_LOOKUP_COUNTRY,
  NamedOption,
  PlaceLocation,
  TexasSelection,
  getCounties,
  getCountyLocation,
  getPlssLocation,
  getPlssRanges,
  getPlssSections,
  getPlssTownships,
  getTexasLocation,
  getTexasOptions
} from "api/map";

import { FALLBACK_ZOOM } from "../utils/resolveJumpCamera";

/**
 * One survey system per region, written out here rather than branched on where the form is
 * drawn. Each one says which levels it has, in order, what fills them, when the selection
 * names a place, and how to resolve it. Adding a system is adding an entry.
 */

/**
 * Every level any form can offer. The region itself is chosen above these. The list is the
 * one statement of it and the name type is read off the list, so a level cannot be named in
 * the type and left out of the list an empty selection is built from.
 */
export const CASCADE_FIELDS = [
  "county",
  "township",
  "range",
  "section",
  "abstract",
  "block"
] as const;

export type CascadeFieldName = (typeof CASCADE_FIELDS)[number];

/** What the user has picked, across every level of every form. */
export type JumpToLocationSelection = {
  region: Nullable<string>;
} & Record<CascadeFieldName, Nullable<string>>;

export interface GridLevel {
  field: CascadeFieldName;
  /** The placeholder, which doubles as the label once the level has a value. */
  label: string;
  /** Named in the plural, for the message a failed list shows. */
  plural: string;
  /** The levels that have to hold a value before this one can be asked for. */
  parents: CascadeFieldName[];
  /** What an empty list means here, in the user's terms. */
  emptyMessage: string;
  /**
   * Levels this one replaces rather than sits under. Only Texas has any: an abstract and a
   * block are two ways of naming the same ground, so choosing either forgets the other.
   */
  replaces?: CascadeFieldName[];
  fetch: (selection: JumpToLocationSelection) => Promise<NamedOption[]>;
}

export interface GridForm {
  levels: GridLevel[];
  /** True once the selection names one place, which is when Go can be pressed. */
  canSubmit: (selection: JumpToLocationSelection) => boolean;
  resolvePlace: (selection: JumpToLocationSelection) => Promise<PlaceLocation>;
  /** Used only when the resolved bounding box cannot be trusted. */
  fallbackZoom: (selection: JumpToLocationSelection) => number;
}

const COUNTY_LEVEL: GridLevel = {
  field: "county",
  label: "County",
  plural: "counties",
  parents: [],
  emptyMessage: "No counties available",
  fetch: (selection) => getCounties(LOCATION_LOOKUP_COUNTRY, selection.region)
};

function toTexasSelection(selection: JumpToLocationSelection): TexasSelection {
  return {
    geoid: selection.county,
    abstract: selection.abstract,
    block: selection.block,
    section: selection.section
  };
}

const PLSS_FORM: GridForm = {
  levels: [
    COUNTY_LEVEL,
    {
      field: "township",
      label: "Township",
      plural: "townships",
      parents: ["county"],
      emptyMessage: "No township/range grid in this county",
      fetch: (selection) => getPlssTownships(selection.county)
    },
    {
      field: "range",
      label: "Range",
      plural: "ranges",
      parents: ["county", "township"],
      emptyMessage: "No ranges available",
      fetch: (selection) => getPlssRanges(selection.county, selection.township)
    },
    {
      field: "section",
      label: "Section",
      plural: "sections",
      parents: ["county", "township", "range"],
      emptyMessage: "No sections available",
      fetch: (selection) =>
        getPlssSections(selection.county, selection.township, selection.range)
    }
  ],
  // A township with no range names several places at once, so it is not a jump yet.
  canSubmit: (selection) =>
    Boolean(selection.county && (!selection.township || selection.range)),
  resolvePlace: (selection) =>
    selection.township
      ? getPlssLocation({
          geoid: selection.county,
          township: selection.township,
          range: selection.range,
          section: selection.section
        })
      : getCountyLocation(selection.county),
  fallbackZoom: (selection) => {
    if (selection.section) return FALLBACK_ZOOM.section;
    if (selection.township) return FALLBACK_ZOOM.township;
    return FALLBACK_ZOOM.county;
  }
};

// Texas is the one form that forks. An abstract and a block are alternatives under the
// county, and a section is inside a block, so a section without a block names nothing.
// The county is a place on its own, and each level under it narrows the jump further.
const TEXAS_FORM: GridForm = {
  levels: [
    COUNTY_LEVEL,
    {
      field: "abstract",
      label: "Abstract",
      plural: "abstracts",
      parents: ["county"],
      emptyMessage: "No abstracts in this county",
      fetch: (selection) => getTexasOptions("abstract", { geoid: selection.county })
    },
    {
      field: "block",
      label: "Block",
      plural: "blocks",
      parents: ["county"],
      emptyMessage: "No blocks in this county",
      replaces: ["abstract"],
      fetch: (selection) => getTexasOptions("block", { geoid: selection.county })
    },
    {
      field: "section",
      label: "Section",
      plural: "sections",
      parents: ["county", "block"],
      emptyMessage: "No sections available",
      fetch: (selection) =>
        getTexasOptions("section", { geoid: selection.county, block: selection.block })
    }
  ],
  canSubmit: (selection) =>
    Boolean(selection.county && (!selection.section || selection.block)),
  // The Texas lookup names an abstract, a block or a section inside a county, so a county
  // on its own is resolved as the county it is.
  resolvePlace: (selection) =>
    selection.abstract || selection.block
      ? getTexasLocation(toTexasSelection(selection))
      : getCountyLocation(selection.county),
  fallbackZoom: (selection) => {
    if (selection.section) return FALLBACK_ZOOM.section;
    if (selection.abstract || selection.block) return FALLBACK_ZOOM.township;
    return FALLBACK_ZOOM.county;
  }
};

// A state with no survey grid of its own stops at its counties.
const COUNTY_FORM: GridForm = {
  levels: [COUNTY_LEVEL],
  canSubmit: (selection) => Boolean(selection.county),
  resolvePlace: (selection) => getCountyLocation(selection.county),
  fallbackZoom: () => FALLBACK_ZOOM.county
};

export const GRID_FORMS: Record<GridType, GridForm> = {
  plss: PLSS_FORM,
  texas: TEXAS_FORM,
  county: COUNTY_FORM
};

/** The form a region is surveyed by, or nothing while no region is chosen. */
export function gridFormFor(gridType: Nullable<GridType>): Nullable<GridForm> {
  return gridType ? GRID_FORMS[gridType] ?? null : null;
}

/**
 * The levels to forget when one of them is chosen: everything under it, plus anything it
 * replaces. Choosing a region forgets all of them, because the form itself changes.
 */
export function fieldsClearedBy(
  form: Nullable<GridForm>,
  field: CascadeFieldName
): CascadeFieldName[] {
  if (!form) return [];
  const index = form.levels.findIndex((level) => level.field === field);
  if (index < 0) return [];
  const below = form.levels.slice(index + 1).map((level) => level.field);
  return [...below, ...(form.levels[index].replaces ?? [])];
}
